import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  MailFilled,
  SendOutlined
} from "@ant-design/icons"
import { Button, type ButtonProps, Flex, Tooltip } from "antd"
import React, { useState } from "react"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { isHeldForSigning } from "../../../../Esign/helpers/signable-record.ts"
import { PROPOSAL_STATUSES } from "../../../../Proposals/constants/proposal-statuses-keys.ts"
import type {
  LeadDocumentAttachment,
  LeadProposal
} from "../../../types/lead.documents.types.ts"

type ActionKey =
  | "duplicate"
  | "view_pdf"
  | "download"
  | "edit"
  | "mark_as_sent"
  | "share"
  | "send_to_review"
  | "view_proposal"

interface ActionConfig {
  key: ActionKey
  title: string
  icon: React.ReactNode
  onClick?: () => void | Promise<void>
  href?: string
  link?: string
  variant?: ButtonProps["variant"]
  color?: ButtonProps["color"]
  visible?: boolean
  hasLoading?: boolean
}

interface LeadViewProposalItemActionsProps {
  proposal: LeadProposal
  isUpdateAllowed: boolean
  viewOnly: boolean
  isUploadedFile: boolean
  onViewProposal: (proposal: LeadProposal) => void
  onShare: (proposal: LeadProposal) => void
  onDuplicateProposal: (proposal: LeadProposal) => void
  onMarkAsSent: (proposalId: string) => void
  onViewPDF: (attachment: LeadDocumentAttachment) => void
  onSendToReview: (proposalId: string, attachmentId: string) => Promise<void>
}

export function LeadViewProposalItemActions({
  proposal,
  isUpdateAllowed,
  viewOnly,
  isUploadedFile,
  onViewProposal,
  onShare,
  onDuplicateProposal,
  onMarkAsSent,
  onViewPDF,
  onSendToReview
}: LeadViewProposalItemActionsProps) {
  const [loadingAction, setLoadingAction] = useState<ActionKey | "">("")
  const isCancelled = proposal.status === PROPOSAL_STATUSES.CANCELLED

  const latestAttachment =
    proposal?.latest_attachment || proposal?.attachments?.at(-1)
  const heldForSigning = isHeldForSigning(proposal)
  const isApprovedWithFile =
    proposal.status === PROPOSAL_STATUSES.APPROVED &&
    !!proposal?.submitted_attachment
  // A proposal out for signature stays approved, so its status alone would still offer this.
  const canMarkAsSent = isApprovedWithFile && !heldForSigning

  const canSubmitForReview = proposal?.can_submit_for_review && isUpdateAllowed
  const isLoading = !!loadingAction
  // const isUploaded = proposal?.is_generated

  // Anything other than an explicit `false` is treated as editable — a
  // legacy (V1) row that predates this field being populated must not lose
  // its Edit button. A superseded version (`is_editable: false`) still has
  // View/Download; only Edit and Duplicate (branching a new version off a
  // non-latest one makes no sense in a linear version history) are gated.
  const isEditable = proposal?.is_editable !== false

  // A proposal sent back for revision is not editable in place (the backend
  // only edits drafts), but branching a new version off it is exactly how it
  // gets revised — create-new-version refuses only superseded versions.
  const isLatestUnderRevision =
    proposal.status === PROPOSAL_STATUSES.DECLINED &&
    proposal?.is_latest_version !== false
  const canDuplicate = isEditable || isLatestUnderRevision

  const editHref = proposal?.is_version_2
    ? RoutesPaths.proposalDocument.edit(proposal?.id)
    : RoutesPaths.proposal.edit(proposal?.id)

  const actions: ActionConfig[] = latestAttachment
    ? [
        {
          key: "duplicate",
          title: "Duplicate latest version",
          icon: <CopyOutlined />,
          onClick: () => onDuplicateProposal(proposal),
          hasLoading: true,
          visible:
            !viewOnly &&
            !isCancelled &&
            !isUploadedFile &&
            isUpdateAllowed &&
            canDuplicate
        },
        {
          key: "view_pdf",
          title: "View latest version",
          icon: <EyeOutlined />,
          onClick: () => onViewPDF(latestAttachment),
          variant: "outlined"
        },
        {
          key: "download",
          title: "Download latest version",
          icon: <DownloadOutlined />,
          href: latestAttachment?.file_url
        },
        {
          key: "edit",
          title: "Edit latest version",
          icon: <EditOutlined />,
          link: editHref,
          visible:
            !viewOnly && canSubmitForReview && !isUploadedFile && isEditable
        },
        {
          key: "send_to_review",
          title: "Send to review latest version",
          icon: <SendOutlined />,
          onClick: () => onSendToReview(proposal?.id, latestAttachment?.id),
          variant: "outlined",
          color: "primary",
          visible: !viewOnly && canSubmitForReview,
          hasLoading: true
        },
        {
          key: "mark_as_sent",
          title: "Mark as sent",
          icon: <FileDoneOutlined />,
          onClick: () => onMarkAsSent(proposal?.id),
          variant: "outlined",
          color: "cyan",
          visible: !viewOnly && canMarkAsSent,
          hasLoading: true
        },
        {
          key: "share",
          title: "Share via email",
          icon: <MailFilled />,
          onClick: () => onShare(proposal),
          variant: "outlined",
          color: "cyan",
          visible:
            !heldForSigning &&
            (isApprovedWithFile ||
              ([PROPOSAL_STATUSES.SENT_TO_CLIENT] as string[])?.includes(
                proposal?.status
              )),
          hasLoading: true
        }
      ]
    : [
        {
          key: "duplicate",
          title: "Duplicate",
          icon: <CopyOutlined />,
          onClick: () => onDuplicateProposal(proposal),
          hasLoading: true,
          visible: !viewOnly && !isCancelled && isUpdateAllowed && canDuplicate
        },
        {
          key: "view_proposal",
          title: "View",
          icon: <EyeOutlined />,
          onClick: () => onViewProposal(proposal),
          variant: "outlined",
          color: "primary",
          hasLoading: true
        },
        {
          key: "edit",
          title: "Edit",
          icon: <EditOutlined />,
          link: editHref,
          visible: !viewOnly && isUpdateAllowed && isEditable
        }
      ]

  const handleClick = async (action: ActionConfig, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!action.onClick) return

    if (action.hasLoading) {
      setLoadingAction(action.key)
      try {
        await action.onClick()
      } finally {
        setLoadingAction("")
      }
    } else {
      action.onClick()
    }
  }

  const renderButton = (action: ActionConfig) => {
    const isActionLoading = loadingAction === action.key
    const disabled = action.hasLoading
      ? isLoading && !isActionLoading
      : isLoading

    return action.link ? (
      <Link to={action.link} onClick={(e) => e.stopPropagation()}>
        <Button
          icon={action.icon}
          variant={action.variant}
          color={action.color}
          disabled={disabled}
          loading={action.hasLoading && isActionLoading}
        />
      </Link>
    ) : (
      <Button
        icon={action.icon}
        onClick={(e) => handleClick(action, e)}
        href={action.href}
        target={action.href ? "_blank" : undefined}
        rel={action.href ? "noreferrer" : undefined}
        download={!!action.href}
        variant={action.variant}
        color={action.color}
        disabled={disabled}
        loading={action.hasLoading && isActionLoading}
      />
    )
  }

  return (
    <Flex gap="small" wrap className="shrink-0">
      {actions
        .filter((action) => action.visible !== false)
        .map((action) => (
          <Tooltip key={action.key} title={action.title}>
            {renderButton(action)}
          </Tooltip>
        ))}
    </Flex>
  )
}
